import React from 'react';
import { ArrowRight } from 'lucide-react';

const HeroSection = () => {
  return (
    <section 
      className="relative overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full background with gradient fade to white */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, #764ba2 0%, #764ba2 70%, white 100%)'
        }}
        aria-hidden="true"
      />
      
      {/* Primary animated gradient layer - only covers top portion */}
      <div 
        className="absolute inset-0 hero-gradient-primary"
        style={{ 
          maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 90%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 90%)'
        }}
        aria-hidden="true"
      />
      
      {/* Secondary glow layers - masked to fade out */}
      <div 
        className="hero-glow-violet" 
        style={{ 
          maskImage: 'linear-gradient(to bottom, black 0%, black 50%, transparent 80%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 50%, transparent 80%)'
        }}
        aria-hidden="true" 
      />
      <div 
        className="hero-glow-steel" 
        style={{ 
          maskImage: 'linear-gradient(to bottom, black 0%, black 50%, transparent 80%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 50%, transparent 80%)'
        }}
        aria-hidden="true" 
      />
      
      {/* Content container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-32 lg:pt-44 pb-48 min-h-[90vh] flex items-center">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 w-full">
          
          {/* Left - Text Content */}
          <div className="flex-1 text-left">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6 drop-shadow-lg">
              Build with confidence.
            </h1>

            <p className="text-lg text-white/70 mb-8 max-w-md">
              Navigate your building project with clarity, confidence and control. From planning through to progress.
            </p>

            {/* App Store Buttons - Colorful official badges */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <a 
                href="https://apps.apple.com/au/app/buildpilot/id6477372083" 
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-appstore-btn"
              >
                <img 
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" 
                  alt="Download on the App Store" 
                  className="h-[44px] w-auto"
                />
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US" 
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-playstore-btn"
              >
                <img 
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                  alt="Get it on Google Play" 
                  className="h-[64px] w-auto -my-2"
                />
              </a>
            </div>

            {/* Powered by badge */}
            <div className="flex items-center gap-2 text-sm text-white/60">
              <div 
                className="w-2 h-2 rounded-full animate-pulse" 
                style={{ background: 'linear-gradient(135deg, #667eea, #f093fb)' }} 
              />
              <span>Powered by BuildPilot App Intelligence</span>
            </div>
          </div>

          {/* Right - Phone Mockups */}
          <div className="flex-1 relative">
            <div className="relative w-full max-w-xl mx-auto flex items-end justify-center min-h-[350px] sm:min-h-[500px] px-2 sm:px-4">
              
              {/* Left Phone */}
              <div className="relative z-10 w-28 sm:w-32 md:w-36 lg:w-44 opacity-80 animate-float-delayed -mr-4 sm:-mr-4 mb-6 sm:mb-0">
                <img 
                  src="/buildpilot-app-toolbox.webp" 
                  alt="BuildPilot App toolbox with building tools for Australian home builders"
                  className="w-full h-auto drop-shadow-2xl"
                  loading="lazy"
                  width="176"
                  height="360"
                />
              </div>

              {/* Main Phone - Center */}
              <div className="relative z-20 w-36 sm:w-44 md:w-48 lg:w-56 animate-float">
                <img 
                  src="/buildpilot-app-home-building-dashboard.webp" 
                  alt="BuildPilot App dashboard with building cost tracker and progress overview"
                  className="w-full h-auto drop-shadow-2xl"
                  loading="eager"
                  width="224"
                  height="458"
                />
              </div>

              {/* Right Phone */}
              <div className="relative z-10 w-28 sm:w-32 md:w-36 lg:w-44 opacity-80 animate-float -ml-4 sm:-ml-4 mb-6 sm:mb-0">
                <img 
                  src="/buildpilot-app-milestones-tracker.webp" 
                  alt="BuildPilot App milestones tracker for home construction progress"
                  className="w-full h-auto drop-shadow-2xl"
                  loading="lazy"
                  width="176"
                  height="360"
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
